/**
 * Admin CRM page — manage leads, send AI-assisted Spanish replies, drip program.
 *
 * Features:
 *  - Filter leads by lang/status, see lead detail
 *  - AI translator: paste English → get Spanish ready to send
 *  - AI draft reply: generate full personalized reply for a lead
 *  - Send reply (email)
 *  - Enroll Spanish leads in drip campaign (4-step sequence)
 *  - View email history per lead
 *
 * Front-desk uses this every day to respond to ES leads without speaking Spanish.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('adminToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const StatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-amber-100 text-amber-800',
    booked: 'bg-emerald-100 text-emerald-800',
    lost: 'bg-gray-200 text-gray-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colors[status] || colors.new}`}>
      {status || 'new'}
    </span>
  );
};

const TranslatorAssist = () => {
  const [text, setText] = useState('');
  const [direction, setDirection] = useState('en_to_es');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const translate = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/crm/translate-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text, direction }),
      });
      const d = await r.json();
      setResult(d);
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (result?.translated) {
      navigator.clipboard.writeText(result.translated);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="translator-assist">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">AI Translator Assist</h2>
          <p className="text-gray-600 text-sm">
            Type your English reply → get Mexican-American Spanish ready to paste. Powered by Claude Sonnet 4.5.
          </p>
        </div>
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1 text-sm"
          data-testid="translator-direction"
        >
          <option value="en_to_es">English → Spanish</option>
          <option value="es_to_en">Spanish → English</option>
        </select>
      </div>
      <textarea
        data-testid="translator-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Hi Maria, I have an opening Monday at 2 PM. Would that work for you?"
        className="w-full border border-gray-300 rounded p-3 text-sm min-h-[100px]"
      />
      <div className="flex gap-2 mt-2">
        <button
          data-testid="translator-go-btn"
          onClick={translate}
          disabled={busy || !text.trim()}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
        >
          {busy ? 'Translating…' : 'Translate'}
        </button>
        {result?.translated && (
          <button
            data-testid="translator-copy-btn"
            onClick={copy}
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded font-semibold text-sm"
          >
            Copy
          </button>
        )}
      </div>
      {result?.translated && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-3">
          <p className="text-xs uppercase text-emerald-700 font-semibold mb-1">Translation</p>
          <p className="text-gray-800 whitespace-pre-wrap">{result.translated}</p>
        </div>
      )}
    </div>
  );
};

const LeadRow = ({ lead, onSelect }) => {
  return (
    <tr
      onClick={() => onSelect(lead)}
      className="cursor-pointer hover:bg-gray-50 border-b"
      data-testid={`lead-row-${lead.id}`}
    >
      <td className="py-2 px-3 font-mono text-xs">{lead.lang === 'es' ? '🇲🇽' : '🇺🇸'}</td>
      <td className="py-2 px-3 font-semibold">{lead.name || '—'}</td>
      <td className="py-2 px-3 text-sm">{lead.email || '—'}</td>
      <td className="py-2 px-3 text-sm">{lead.phone || '—'}</td>
      <td className="py-2 px-3 text-xs font-mono text-gray-500 truncate max-w-xs" title={lead.page}>
        {lead.page || '—'}
      </td>
      <td className="py-2 px-3"><StatusBadge status={lead.status} /></td>
      <td className="py-2 px-3 text-xs text-gray-400">
        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
      </td>
    </tr>
  );
};

const LeadDetail = ({ lead, onClose, onChange }) => {
  const [intent, setIntent] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [busy, setBusy] = useState(null);
  const [emails, setEmails] = useState([]);

  useEffect(() => {
    if (!lead) return;
    fetch(`${API}/api/admin/crm/leads/${lead.id}/emails`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setEmails(d.emails || []));
  }, [lead]);

  const setStatus = async (status) => {
    setBusy('status');
    try {
      const r = await fetch(`${API}/api/admin/crm/leads/${lead.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        const d = await r.json();
        onChange(d.lead);
      }
    } finally { setBusy(null); }
  };

  const draft = async () => {
    if (!intent.trim()) return;
    setBusy('draft');
    try {
      const r = await fetch(`${API}/api/admin/crm/draft-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ lead_id: lead.id, intent, tone: 'warm' }),
      });
      if (r.ok) {
        const d = await r.json();
        setDraftSubject(d.subject);
        setDraftBody(d.body);
      }
    } finally { setBusy(null); }
  };

  const send = async () => {
    if (!draftBody.trim() || !draftSubject.trim()) return;
    setBusy('send');
    try {
      const r = await fetch(`${API}/api/admin/crm/send-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ lead_id: lead.id, subject: draftSubject, body: draftBody }),
      });
      if (r.ok) {
        setDraftBody('');
        setDraftSubject('');
        setIntent('');
        const refreshed = await fetch(`${API}/api/admin/crm/leads/${lead.id}/emails`, { headers: authHeaders() });
        if (refreshed.ok) setEmails((await refreshed.json()).emails || []);
        onChange({ ...lead, status: 'contacted' });
      }
    } finally { setBusy(null); }
  };

  const enrollDrip = async () => {
    setBusy('drip');
    try {
      await fetch(`${API}/api/admin/crm/drip/enroll/${lead.id}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      alert('Enrolled in 4-step Spanish drip program');
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-[90%] max-w-3xl max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-detail"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold">{lead.name || 'Anonymous'}</h2>
            <p className="text-gray-600 text-sm">
              {lead.email} · {lead.phone || 'no phone'} · {lead.lang === 'es' ? 'Spanish' : 'English'}
            </p>
            <p className="text-gray-400 text-xs font-mono mt-1">From: {lead.page}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="flex gap-2 mb-4">
          {['new', 'contacted', 'booked', 'lost'].map((s) => (
            <button
              key={s}
              data-testid={`status-${s}-btn`}
              onClick={() => setStatus(s)}
              disabled={busy === 'status'}
              className={`px-3 py-1 rounded text-xs font-semibold ${
                lead.status === s
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
          {lead.lang === 'es' && (
            <button
              data-testid="enroll-drip-btn"
              onClick={enrollDrip}
              disabled={busy === 'drip'}
              className="ml-auto bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-semibold"
            >
              Enroll in Drip
            </button>
          )}
        </div>

        <div className="bg-gray-50 rounded p-4 mb-4">
          <p className="text-xs uppercase text-gray-500 font-semibold mb-2">AI-Drafted Reply</p>
          <input
            data-testid="draft-intent"
            type="text"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder="What do you want to say? (English) e.g., 'confirm Mon 2pm appointment'"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
          />
          <button
            data-testid="draft-go-btn"
            onClick={draft}
            disabled={busy === 'draft' || !intent.trim()}
            className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded text-sm font-semibold disabled:bg-gray-300"
          >
            {busy === 'draft' ? 'Drafting…' : `Draft ${lead.lang === 'es' ? 'Spanish' : 'English'} reply`}
          </button>
          {draftBody && (
            <div className="mt-3">
              <input
                data-testid="draft-subject"
                value={draftSubject}
                onChange={(e) => setDraftSubject(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-semibold mb-2"
              />
              <textarea
                data-testid="draft-body"
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                className="w-full border border-gray-300 rounded p-3 text-sm min-h-[200px]"
              />
              <button
                data-testid="send-reply-btn"
                onClick={send}
                disabled={busy === 'send'}
                className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
              >
                {busy === 'send' ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded p-4">
          <p className="text-xs uppercase text-gray-500 font-semibold mb-2">Email History</p>
          {emails.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No emails sent yet.</p>
          ) : (
            <ul className="space-y-2">
              {emails.map((e) => (
                <li key={e.id} className="text-sm border-b pb-2">
                  <p className="font-semibold">{e.subject}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(e.sent_at).toLocaleString()} · by {e.sent_by} · {e.status}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminCRM = () => {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState({ lang: '', status: '' });
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({ total: 0 });

  const refresh = async () => {
    const params = new URLSearchParams();
    if (filter.lang) params.set('lang', filter.lang);
    if (filter.status) params.set('status', filter.status);
    params.set('limit', '100');
    const r = await fetch(`${API}/api/admin/crm/leads?${params}`, { headers: authHeaders() });
    if (r.ok) {
      const d = await r.json();
      setLeads(d.leads || []);
      setStats({ total: d.total || 0 });
    }
  };

  useEffect(() => { refresh(); }, [filter.lang, filter.status]);

  const onLeadChange = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
    setSelected((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-crm-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">CRM — Leads & Replies</h1>
        <p className="text-gray-600 mt-2">
          Track leads, send AI-assisted Spanish replies, run drip campaigns. {stats.total} leads loaded.
        </p>
      </div>

      <TranslatorAssist />

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex gap-3 p-4 border-b items-center">
          <select
            data-testid="filter-lang"
            value={filter.lang}
            onChange={(e) => setFilter({ ...filter, lang: e.target.value })}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">All languages</option>
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </select>
          <select
            data-testid="filter-status"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="booked">Booked</option>
            <option value="lost">Lost</option>
          </select>
          <button
            data-testid="refresh-leads-btn"
            onClick={refresh}
            className="ml-auto bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded text-sm font-semibold"
          >
            Refresh
          </button>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Lang</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Name</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Email</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Phone</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Landing</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Status</th>
              <th className="py-2 px-3 text-left text-xs uppercase text-gray-500">Date</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan="7" className="py-6 text-center text-gray-400 italic">No leads match filters.</td></tr>
            ) : leads.map((l) => <LeadRow key={l.id} lead={l} onSelect={setSelected} />)}
          </tbody>
        </table>
      </div>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onChange={onLeadChange} />}
    </div>
  );
};

export default AdminCRM;
